import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, Subject } from 'rxjs';
import { environment } from '@environments/environment';
import { switchMap, take, filter, distinctUntilChanged, shareReplay } from 'rxjs/operators';
import { IResponseObjectSuccess, IResponseSuccess } from '../../interfaces';
import { 
  Store as StoreModel, 
  Client, 
  ClientUserForm, 
  Address, 
  ClientUser, 
  Authentication 
} from '@core/models';
import { Store } from '@ngrx/store';
import { AppState } from '@store/reducers';
import { selectStore } from '@store/selectors';
import { selectClient } from '@store/selectors';
import { transformToClientUpdate } from '@core/helpers';
import { AuthService } from '../auth/auth.service';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  store$: Observable<StoreModel | null>;
  client$: Observable<Client | null>;

  private closeDialogSource = new Subject<void>();
  closeDialog$ = this.closeDialogSource.asObservable();

  private creationCompletedSource = new Subject<void>();
  creationCompleted$ = this.creationCompletedSource.asObservable();

  private updateCompletedSource = new Subject<void>();
  updateCompleted$ = this.updateCompletedSource.asObservable();

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private authService: AuthService
  ) {
    this.store$ = this.store.select(selectStore);
    this.client$ = this.store.select(selectClient);
  }

  creationCompleted(): void {
    this.creationCompletedSource.next();
    this.closeDialog();
  }

  updateCompleted(): void {
    this.updateCompletedSource.next();
    this.closeDialog();
  }

  closeDialog(): void {
    this.closeDialogSource.next();
  }

  getUser(): Observable<ClientUser | null> {
    return this.client$.pipe(
      map(client => {
        return client?.user ?? null;
      })
    );
  }

  update(clientForm: ClientUserForm, clientId: string): Observable<IResponseSuccess<Client>> {
    return this.store$.pipe(
      filter((store): store is StoreModel => !!store),
      filter(() => !!clientForm && !!clientId),
      switchMap(store => {
        return this.http.patch<IResponseSuccess<Client>>(
          `${environment.microSeguridades}/pos/cliente/${clientId}`,
          transformToClientUpdate({
            ...clientForm,
            tienda_id: store.idLocal,
            estado_id: store?.estado_id as string,
            cuenta_id: store?.cuenta_id as string,
          }),
          {
            headers: {
              authorization: store.apiKey,
            },
          }
        );
      }),
      take(1)
    );
  }

  getLocation(): Observable<GeolocationPosition> {
    return new Observable(observer => {
      if (!navigator.geolocation) {
        observer.error('La geolocalización no está soportada por este navegador');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        position => {
          observer.next(position);
          observer.complete();
        },
        error => {
          let mensajeError = '';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              mensajeError = 'User denied the request for Geolocation.';
              break;
            case error.POSITION_UNAVAILABLE:
              mensajeError = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              mensajeError = 'The request to get user location timed out.';
              break;
            default:
              mensajeError = 'An unknown error occurred while getting the location.';
          }
          observer.error(mensajeError);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 5000,
        }
      );
    });
  }

  getAddress(): Observable<Address[]> {
    return this.client$.pipe(
      map(client => {
        return client?.direcciones ?? [];
      }),
    );
  }

  getAddressById(id: string): Observable<Address | null> {
    return this.client$.pipe(
      filter((client): client is Client => !!client),
      map(client => {
        return (client?.direcciones ?? []).find(address => address.idDireccion == id) ?? null;
      }),
      take(1)
    );
  }

  find(client: Authentication | null): Observable<IResponseSuccess<Client>> {
    return this.store$.pipe(
      // filter((store): store is StoreModel => !!store),
      filter(() => !!client),
      switchMap(store => {
        return this.http.get<IResponseSuccess<Client>>(`${environment.microSeguridades}/pos/cliente/${client?.clienteId}`, {
          headers: {
            // authorization: store.apiKey,
            authorization: `Bearer ${client?.token}`,
          },
        });
      }),
      take(1)
    );
  }

  create(clientAddressForm: Address, client_id: string): Observable<IResponseSuccess<Client>> {
    return this.store$.pipe(
      // filter((store): store is StoreModel => !!store),
      filter(() => !!clientAddressForm && !!client_id),
      switchMap(_ => {
        return this.http.post<IResponseSuccess<Client>>(
          `${environment.microSeguridades}/pos/cliente/${client_id}/direcciones`,
          clientAddressForm,
          {
            headers: {
              authorization: `Bearer ${this.authService.getToken()}`,
            },
          }
        );
      }),
      take(1)
    );
  }

  updateAddress(clientAddressForm: Address, idAddress: string): Observable<IResponseSuccess<Client>> {
    return this.store$.pipe(
      filter((store): store is StoreModel => !!store),
      filter(() => !!clientAddressForm && !!idAddress),
      switchMap(store => {
        return this.http.patch<IResponseSuccess<Client>>(
          `${environment.microSeguridades}/pos/cliente/direcciones/${idAddress}`,
          clientAddressForm,
          {
            headers: {
              authorization: `Bearer ${this.authService.getToken()}`,
            },
          }
        );
      }),
      take(1)
    );
  }

  remove(idAddress: string | null): Observable<IResponseSuccess<Client>> {
    return this.store$.pipe(
      filter((store): store is StoreModel => !!store),
      filter(() => !!idAddress),
      switchMap(store => {
        return this.http.delete<IResponseSuccess<Client>>(
          `${environment.microSeguridades}/pos/cliente/direcciones/${idAddress}`,
          {
            headers: {
              authorization: `Bearer ${this.authService.getToken()}`,
            },
          }
        );
      }),
      take(1)
    );
  }

  updateDefault(idAddress: string): Observable<IResponseSuccess<Client>> {
    return this.store$.pipe(
      filter((store): store is StoreModel => !!store),
      filter(() => !!idAddress),
      switchMap(store => {
        return this.http.patch<IResponseSuccess<Client>>(
          `${environment.microSeguridades}/pos/cliente/direcciones/${idAddress}/por-defecto`,
          {},
          {
            headers: {
              authorization: `Bearer ${this.authService.getToken()}`,
            },
          }
        );
      }),
      take(1)
    );
  }
}
